import React, { useState, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Text } from '@reservamos/elements';
import Cookies from 'js-cookie';
import { trackEvent } from 'user-analytics';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { PRIVACY_BANNER_REJECTED } from 'constants/TrackEvents';
import cn from 'utils/cn';
import errorMonitoring from '../../errorMonitoring';

const PRIVACY_BANNER_COOKIE = 'privacy-banner-is-closed';
const PRIVACY_BANNER_COOKIE_EXPIRES_DAYS = 30;
const PRIVACY_BANNER_DISMISSED_VALUES = ['true', 'accepted', 'rejected'];
const BANNER_CLASSES = cn(
  'fixed bottom-3 left-1/2 z-10 w-[calc(100%-24px)] max-w-[1232px] -translate-x-1/2',
  'rounded-[12px] bg-[#161616] p-4 shadow-[0_18px_45px_rgba(0,0,0,0.25)]',
  'sm:bottom-4 sm:w-[calc(100%-32px)] sm:px-6 sm:py-[18px]',
);
const BANNER_CONTENT_CLASSES = cn(
  'flex flex-col items-start gap-4',
  'sm:flex-row sm:items-center sm:justify-between sm:gap-6',
);
const BANNER_BODY_CLASSES = 'min-w-0 flex-1';
const BANNER_ACTIONS_CLASSES = cn(
  'flex w-full flex-col items-stretch gap-3',
  'sm:w-auto sm:flex-shrink-0 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end',
);
const BANNER_BUTTON_CLASSES = cn(
  'inline-flex min-h-11 w-full items-center justify-center whitespace-nowrap rounded-lg border',
  'border-white/20 px-[18px] text-sm font-semibold text-white no-underline transition-colors',
  'hover:text-white focus:text-white sm:w-auto',
);
const BANNER_PRIMARY_BUTTON_CLASSES = cn(BANNER_BUTTON_CLASSES, 'border-[#0052cc] bg-[#0052cc]');
const BANNER_SECONDARY_BUTTON_CLASSES = cn(BANNER_BUTTON_CLASSES, 'bg-transparent');
const BANNER_LINK_BUTTON_CLASSES = cn(
  BANNER_BUTTON_CLASSES,
  'border-[rgba(49,130,246,0.45)] text-[#60a5fa]',
);
const INLINE_LINK_CLASSES = 'font-semibold text-white underline hover:text-white focus:text-white';

/**
 * PrivacyBanner component. Displays a banner prompting the user to manage the use of cookies.
 * @returns {JSX.Element|null} A div element with the privacy banner, including a text message and actions to accept, reject, or read the privacy notice.
 */
const PrivacyBanner = () => {
  const [isClosed, setIsClosed] = useState(
    PRIVACY_BANNER_DISMISSED_VALUES.includes(Cookies.get(PRIVACY_BANNER_COOKIE)),
  );
  const { USE_CUSTOMIZE_COOKIES_BANNER = false } = useWhitelabelFeatures();
  const { t, i18n } = useTranslation(['notifications', 'footer']);
  const { copies, env } = useSelector((state) => state.whitelabelConfig);
  const defaultLang = env.lang?.default || 'es-MX';
  const policies = copies?.policies?.[i18n.language || defaultLang];
  const errorNotified = useRef(false);

  useEffect(() => {
    if (!copies?.policies?.[i18n.language] && !errorNotified.current) {
      errorMonitoring.notify({
        error: new Error('No policies found for the current language'),
        info: { from: 'PrivacyBanner', language: i18n.language, copies },
      });
      errorNotified.current = true;
    }
  }, [copies, i18n.language]);

  if (!policies?.noticeOfPrivacy?.url) return null;

  const { noticeOfPrivacy } = policies;
  const { provider } = copies;
  const privacyNoticeLabel = noticeOfPrivacy.label || t('footer:links.privacy_notice');
  /**
   * Closes the privacy banner by setting a cookie to remember the user's choice.
   * @param {string} bannerPreference - Selected cookie preference state.
   * @returns {void}
   */
  const closeBanner = (bannerPreference) => {
    Cookies.set(PRIVACY_BANNER_COOKIE, bannerPreference, {
      expires: PRIVACY_BANNER_COOKIE_EXPIRES_DAYS,
    });
    setIsClosed(true);
  };

  /**
   * Closes the privacy banner without persisting the user's choice.
   * @returns {void}
   */
  const dismissBanner = () => {
    setIsClosed(true);
  };

  /**
   * Tracks the reject action and closes the banner without persisting the choice.
   * @returns {void}
   */
  const handleReject = () => {
    trackEvent(PRIVACY_BANNER_REJECTED);
    dismissBanner();
  };

  if (isClosed) return null;

  return (
    <div className={BANNER_CLASSES}>
      <div className={BANNER_CONTENT_CLASSES}>
        <div className={BANNER_BODY_CLASSES}>
          <Text color="white" size="S" lineHeight="1.6em">
            {USE_CUSTOMIZE_COOKIES_BANNER ? (
              <Trans
                i18nKey="message.customize_cookies_banner"
                ns="notifications"
                components={{
                  privacyLink: (
                    <a
                      className={INLINE_LINK_CLASSES}
                      href={noticeOfPrivacy.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {privacyNoticeLabel}
                    </a>
                  ),
                }}
              />
            ) : (
              <>
                {t('message.agree_to_the_use_of_cookies', { brand: provider?.name })}{' '}
                <a
                  className={INLINE_LINK_CLASSES}
                  href={noticeOfPrivacy.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {privacyNoticeLabel}
                </a>
                .
              </>
            )}
          </Text>
        </div>
        <div className={BANNER_ACTIONS_CLASSES}>
          <button
            type="button"
            className={BANNER_PRIMARY_BUTTON_CLASSES}
            onClick={() => closeBanner(USE_CUSTOMIZE_COOKIES_BANNER ? 'accepted' : 'true')}
          >
            {t('message.agree_to_the_use_of_cookies_button_label')}
          </button>
          {USE_CUSTOMIZE_COOKIES_BANNER && (
            <>
              <button
                type="button"
                className={BANNER_SECONDARY_BUTTON_CLASSES}
                onClick={handleReject}
              >
                {t('message.reject_cookies_button_label')}
              </button>
              <a
                className={BANNER_LINK_BUTTON_CLASSES}
                href={noticeOfPrivacy.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {privacyNoticeLabel}
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivacyBanner;
